import {Node, mergeAttributes} from '@tiptap/core'

export type MediaEmbedOptions = {
  HTMLAttributes: Record<string, any>
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    mediaEmbed: {
      setMediaEmbed: (options: {src: string}) => ReturnType
    }
  }
}

type Provider = {
  name: string
  pattern: RegExp
  iframeSrc: (match: RegExpMatchArray) => string
}

const providers: Provider[] = [
  {
    name: 'YouTube',
    pattern: /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/,
    iframeSrc: (match) => `https://www.youtube.com/embed/${match[1]}`,
  },
  {
    name: 'YouTube Playlist',
    pattern: /^(?:https?:\/\/)?(?:www\.)?youtube\.com\/playlist\?list=([\w-]+)/,
    iframeSrc: (match) =>
      `https://www.youtube.com/embed?listType=playlist&list=${match[1]}`,
  },
  {
    name: 'Vimeo',
    pattern: /^(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(\d+)/,
    iframeSrc: (match) => `https://player.vimeo.com/video/${match[1]}`,
  },
  {
    name: 'Searchie',
    pattern: /(?:searchie\.io|membership\.io)\/watch\/(\w+)/,
    iframeSrc: (match) => `https://app.membership.io/file/${match[1]}/embed`,
  },
  {
    name: 'Wistia',
    pattern: /wistia\.com\/medias\/(\w+)/,
    iframeSrc: (match) => `https://fast.wistia.net/embed/iframe/${match[1]}`,
  },
]

function resolveIframeSrc(url: string): string {
  for (const provider of providers) {
    const match = url.match(provider.pattern)
    if (match) {
      return provider.iframeSrc(match)
    }
  }

  // Fallback: use the URL directly as iframe src
  return url
}

const MediaEmbed = Node.create<MediaEmbedOptions>({
  name: 'mediaEmbed',

  group: 'block',

  atom: true,

  addOptions() {
    return {
      HTMLAttributes: {},
    }
  },

  addAttributes() {
    return {
      src: {
        default: null,
      },
    }
  },

  parseHTML() {
    return [
      {
        // Parse CKEditor media embed output: <figure class="media"><oembed url="..."></oembed></figure>
        tag: 'figure.media',
        getAttrs: (node) => {
          const element = node as HTMLElement
          const oembed = element.querySelector('oembed')
          if (oembed) {
            return {src: oembed.getAttribute('url')}
          }
          return false
        },
      },
      {
        // Parse CKEditor previewsInData output: <figure class="media"><div ...><iframe src="...">
        tag: 'figure.media',
        getAttrs: (node) => {
          const element = node as HTMLElement
          const iframe = element.querySelector('iframe')
          if (iframe) {
            return {src: iframe.getAttribute('src')}
          }
          return false
        },
      },
      {
        // Parse standalone responsive iframe wrapper (CKEditor output with previewsInData)
        tag: 'div',
        getAttrs: (node) => {
          const element = node as HTMLElement
          const style = element.getAttribute('style') || ''
          if (!style.includes('padding-bottom')) {
            return false
          }
          const iframe = element.querySelector('iframe')
          if (iframe) {
            return {src: iframe.getAttribute('src')}
          }
          return false
        },
      },
    ]
  },

  renderHTML({HTMLAttributes}) {
    const src = resolveIframeSrc(HTMLAttributes.src || '')

    return [
      'div',
      {
        'data-media-embed': '',
        style:
          'position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden;',
      },
      [
        'iframe',
        mergeAttributes(this.options.HTMLAttributes, {
          src,
          style:
            'position: absolute; width: 100%; height: 100%; top: 0; left: 0;',
          frameborder: '0',
          allow: 'autoplay; encrypted-media',
          allowfullscreen: 'true',
        }),
      ],
    ]
  },

  addCommands() {
    return {
      setMediaEmbed: (options) => ({commands}) => {
        return commands.insertContent({
          type: this.name,
          attrs: options,
        })
      },
    }
  },

  addNodeView() {
    return ({node}) => {
      const src = resolveIframeSrc(node.attrs.src || '')

      const wrapper = document.createElement('div')
      wrapper.setAttribute('data-media-embed', '')
      wrapper.style.cssText =
        'position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden;'

      const iframe = document.createElement('iframe')
      iframe.src = src
      iframe.style.cssText =
        'position: absolute; width: 100%; height: 100%; top: 0; left: 0;'
      iframe.setAttribute('frameborder', '0')
      iframe.setAttribute('allow', 'autoplay; encrypted-media')
      iframe.setAttribute('allowfullscreen', 'true')

      wrapper.appendChild(iframe)

      return {
        dom: wrapper,
        update: (updatedNode) => {
          if (updatedNode.type.name !== 'mediaEmbed') {
            return false
          }
          const newSrc = resolveIframeSrc(updatedNode.attrs.src || '')
          if (iframe.src !== newSrc) {
            iframe.src = newSrc
          }
          return true
        },
      }
    }
  },
})

export default MediaEmbed
